const FN_BASE = 'https://australia-southeast2-biddle-d46c3.cloudfunctions.net'

/** Crockford base32 alphabet used for mailed campaign codes (no I/L/O/U). */
const CODE_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz'
const CODE_LENGTH = 7

export type CampaignResolveResult = {
  found: boolean
  code?: string
  addressLine1?: string | null
  suburb?: string | null
  postcode?: string | null
  /** Consumer `properties/{id}` when backfilled on the campaign piece. */
  propertyId?: string | null
  isIOS?: boolean
  platform?: string
  error?: string
}

export function normalizeCampaignCode(raw: string): string | null {
  let candidate = raw.trim()

  try {
    const url = new URL(candidate)
    const parts = url.pathname.split('/').filter(Boolean)
    const oIndex = parts.indexOf('o')
    if (oIndex >= 0 && parts[oIndex + 1]) {
      candidate = parts[oIndex + 1]
    } else if (parts.length > 0) {
      candidate = parts[parts.length - 1]!
    }
  } catch {
    // Not a URL — treat as raw code / BDL- reference.
  }

  candidate = candidate.toLowerCase()
  if (candidate.startsWith('bdl-')) candidate = candidate.slice(4)
  candidate = [...candidate].filter((c) => CODE_ALPHABET.includes(c)).join('')

  if (candidate.length !== CODE_LENGTH) return null
  return candidate
}

export function isValidCampaignCode(raw: string): boolean {
  return normalizeCampaignCode(raw) != null
}

/** Title-case street line for `searchPropertiesByPrefix` (matches iOS LetterClaimService). */
export function campaignSearchPrefix(addressLine1: string): string {
  return addressLine1
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

export async function resolveCampaignCode(rawCode: string): Promise<CampaignResolveResult> {
  const code = normalizeCampaignCode(rawCode)
  if (!code) {
    return { found: false, error: 'invalid_code' }
  }

  const url = `${FN_BASE}/resolveCampaignCode?code=${encodeURIComponent(code)}`
  const res = await fetch(url)
  if (res.status === 400) return { found: false, error: 'invalid_code' }
  if (res.status === 404) return { found: false }

  const data = (await res.json()) as CampaignResolveResult
  if (!res.ok || !data.found) return { found: false, error: data.error }
  return data
}

export type CampaignFunnelEvent =
  | 'web_land'
  | 'register_start'
  | 'account_created'
  | 'kyc_started'
  | 'kyc_completed'
  | 'claim_submitted'
  | 'app_redirect'
  | 'in_app_code'

/** Fire-and-forget funnel event. Failures are non-fatal. */
export function campaignEvent(rawCode: string, event: CampaignFunnelEvent | string): void {
  const code = normalizeCampaignCode(rawCode)
  if (!code) return

  void fetch(`${FN_BASE}/campaignEvent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, event }),
  }).catch(() => {
    // Non-fatal
  })
}

export type CampaignWaitlistPayload = {
  code: string
  ownerConfirmed: boolean
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  name?: string
  consent?: boolean
}

/** Optional "verify later" lead capture. Returns false on network/validation failure. */
export async function submitCampaignWaitlist(payload: CampaignWaitlistPayload): Promise<boolean> {
  const code = normalizeCampaignCode(payload.code)
  if (!code) return false

  try {
    const res = await fetch(`${FN_BASE}/submitCampaignWaitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, code }),
    })
    return res.ok
  } catch {
    return false
  }
}

export const BIDDLE_APP_STORE_URL =
  'https://apps.apple.com/au/app/biddle-buy-sell-property/id6758592779'
